import {
  json,
  type LoaderFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction,
  type SerializeFrom,
} from '@remix-run/node'
import { useLoaderData, useSearchParams } from '@remix-run/react'
import { clsx } from 'clsx'
import * as React from 'react'
import { ArticleCard } from '~/components/article-card.tsx'
import { Button } from '~/components/button.tsx'
import { ServerError } from '~/components/errors.tsx'
import { Grid } from '~/components/grid.tsx'
import { PlusIcon, SearchIcon } from '~/components/icons.tsx'
import { FeaturedSection } from '~/components/sections/featured-section.tsx'
import { HeroSection } from '~/components/sections/hero-section.tsx'
import { Tag } from '~/components/tag.tsx'
import { H6 } from '~/components/typography.tsx'
import {
  getImageBuilder,
  getSocialImageWithPreTitle,
  images,
} from '~/images.tsx'
import { filterPosts } from '~/utils/blog.ts'
import { getBlogRecommendations } from '~/utils/blog.server.ts'
import { getBannerAltProp } from '~/utils/mdx.tsx'
import { getBlogMdxListItems } from '~/utils/mdx.server.ts'
import {
  formatNumber,
  getDisplayUrl,
  getUrl,
  reuseUsefulLoaderHeaders,
  useUpdateQueryStringValueWithoutNavigation,
  useCapturedRouteError,
} from '~/utils/misc.tsx'
import { getSocialMetas } from '~/utils/seo.ts'
import { getServerTimeHeader } from '~/utils/timing.server.ts'
import { useRootData } from '~/utils/use-root-data.ts'
import { type RootLoaderType } from '~/root.tsx'

export const links: LinksFunction = () => {
  return [
    {
      rel: 'alternate',
      type: 'application/rss+xml',
      title: 'Blog - Vinh Nguyen',
      href: '/blog/rss.xml',
    },
  ]
}

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = {}
  const [posts, [recommended]] = await Promise.all([
    getBlogMdxListItems({ request }).then(allPosts =>
      allPosts.filter(p => !p.frontmatter.draft && !p.frontmatter.unlisted),
    ),
    getBlogRecommendations({ request, limit: 1, timings }),
  ])

  const tags = new Set<string>()
  for (const post of posts) {
    for (const category of post.frontmatter.categories ?? []) {
      tags.add(category)
    }
  }

  const data = {
    posts,
    recommended,
    tags: Array.from(tags),
  }

  return json(data, {
    headers: {
      'Cache-Control': 'private, max-age=3600',
      Vary: 'Cookie',
      'Server-Timing': getServerTimeHeader(timings),
    },
  })
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader, { root: RootLoaderType }> = ({
  data,
  matches,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const requestInfo = matches.find(m => m.id === 'root')?.data.requestInfo
  const { posts } = data as SerializeFrom<typeof loader>
  return getSocialMetas({
    title: 'Articles - Vinh Nguyen',
    description: `Boost your development knowledge with ${formatNumber(
      posts.length,
    )} articles on Microservice, Testing, Database, Software Development, and more.`,
    keywords:
      'Microservice, Testing, Database, Software Development, Vinh Nguyen Blog',
    url: getUrl(requestInfo),
    image: getSocialImageWithPreTitle({
      url: getDisplayUrl(requestInfo),
      featuredImage: images.defaultFeaturedImage.id,
      preTitle: 'Check out my articles',
      title:
        'Essential insights, ideas, and experiences for your development work',
    }),
  })
}

const PAGE_SIZE = 12
const initialIndexToShow = PAGE_SIZE

function BlogHome() {
  const { requestInfo } = useRootData()
  const [searchParams] = useSearchParams()

  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const resultsRef = React.useRef<HTMLDivElement>(null)

  /**
   * This is here to make sure that a user doesn't hit "enter" on the search
   * button, which focuses the input and then keyup the enter on the input
   * which will trigger the scroll down. We should *only* scroll when the
   * "enter" keypress and keyup happen on the input.
   */
  const ignoreInputKeyUp = React.useRef<boolean>(false)
  const [queryValue, setQuery] = React.useState<string>(() => {
    return searchParams.get('q') ?? ''
  })
  const query = queryValue.trim()

  useUpdateQueryStringValueWithoutNavigation('q', query)

  const data = useLoaderData<typeof loader>()
  const { posts: allPosts } = data

  const matchingPosts = React.useMemo(() => {
    return filterPosts(allPosts, query)
  }, [allPosts, query])

  const [indexToShow, setIndexToShow] = React.useState(initialIndexToShow)
  React.useEffect(() => {
    setIndexToShow(initialIndexToShow)
  }, [query])

  function toggleTag(tag: string) {
    setQuery(q => {
      const expression = new RegExp(tag, 'ig')

      const newQuery = expression.test(q)
        ? q.replace(expression, '')
        : `${q} ${tag}`

      return newQuery.replace(/\s+/g, ' ').trim()
    })
  }

  const isSearching = query.length > 0

  const posts = isSearching
    ? matchingPosts.slice(0, indexToShow)
    : matchingPosts
        .filter(p => p.slug !== data.recommended?.slug)
        .slice(0, indexToShow)

  const hasMorePosts = isSearching
    ? indexToShow < matchingPosts.length
    : indexToShow < matchingPosts.length - 1

  const visibleTags = isSearching
    ? new Set(
        matchingPosts
          .flatMap(post => post.frontmatter.categories)
          .filter(Boolean),
      )
    : new Set(data.tags)

  const recommendedPermalink = data.recommended
    ? `${requestInfo.origin}/blog/${data.recommended.slug}`
    : undefined

  const searchInputPlaceholder = 'Search articles'

  return (
    <div>
      <HeroSection
        title="Boost your development knowledge with helpful articles."
        animatedTitle
        action={
          <div className="w-full">
            <form
              action="/blog"
              method="GET"
              onSubmit={e => e.preventDefault()}
            >
              <div className="relative">
                <button
                  title={query === '' ? 'Search' : 'Clear search'}
                  type="button"
                  onClick={() => {
                    setQuery('')
                    ignoreInputKeyUp.current = true
                    searchInputRef.current?.focus()
                  }}
                  onKeyDown={() => {
                    ignoreInputKeyUp.current = true
                  }}
                  onKeyUp={() => {
                    ignoreInputKeyUp.current = false
                  }}
                  className={clsx(
                    'absolute left-6 top-0 flex h-full items-center justify-center border-none bg-transparent p-0 text-slate-500',
                    {
                      'cursor-pointer': query !== '',
                      'cursor-default': query === '',
                    },
                  )}
                >
                  <SearchIcon />
                </button>
                <input
                  ref={searchInputRef}
                  type="search"
                  value={queryValue}
                  onChange={event =>
                    setQuery(event.currentTarget.value.toLowerCase())
                  }
                  onKeyUp={e => {
                    if (!ignoreInputKeyUp.current && e.key === 'Enter') {
                      resultsRef.current
                        ?.querySelector('a')
                        ?.focus({ preventScroll: true })
                      resultsRef.current?.scrollIntoView({ behavior: 'smooth' })
                    }
                    ignoreInputKeyUp.current = false
                  }}
                  name="q"
                  placeholder={searchInputPlaceholder}
                  className="text-primary bg-primary border-secondary focus:bg-secondary w-full appearance-none rounded-md border py-4 pl-14 pr-6 text-lg font-medium hover:border-main-current focus:border-main-current focus:outline-none"
                />
              </div>
            </form>
          </div>
        }
      />

      <Grid className="mb-14">
        {data.tags.length > 0 ? (
          <div className="col-span-full -mb-4 -mr-4 flex flex-wrap lg:col-span-10">
            {data.tags.map(tag => {
              const selected = query.includes(tag)
              return (
                <Tag
                  key={tag}
                  tag={tag}
                  selected={selected}
                  onClick={() => toggleTag(tag)}
                  disabled={Boolean(!visibleTags.has(tag)) ? !selected : false}
                />
              )
            })}
          </div>
        ) : null}
      </Grid>

      {!isSearching && data.recommended ? (
        <div className="mb-10">
          <FeaturedSection
            subTitle={[
              data.recommended.dateDisplay,
              data.recommended.readTime?.text ?? 'quick read',
            ]
              .filter(Boolean)
              .join(' — ')}
            title={data.recommended.frontmatter.title}
            blurDataUrl={data.recommended.frontmatter.bannerBlurDataUrl}
            imageBuilder={
              data.recommended.frontmatter.bannerCloudinaryId
                ? getImageBuilder(
                    data.recommended.frontmatter.bannerCloudinaryId,
                    getBannerAltProp(data.recommended.frontmatter),
                  )
                : undefined
            }
            slug={data.recommended.slug}
            permalink={recommendedPermalink}
          />
        </div>
      ) : null}

      <Grid className="mb-64" ref={resultsRef}>
        {posts.length === 0 && isSearching ? (
          <div className="col-span-full flex flex-col items-center">
            <H6 as="p" variant="secondary" className="mt-24 max-w-lg">
              {`Couldn't find anything to match your criteria. Sorry.`}
            </H6>
          </div>
        ) : (
          posts.map(article => (
            <div key={article.slug} className="col-span-4 mb-10">
              <ArticleCard article={article} />
            </div>
          ))
        )}
      </Grid>

      {hasMorePosts ? (
        <div className="mb-64 flex w-full justify-center">
          <Button
            variant="secondary"
            onClick={() => setIndexToShow(i => i + PAGE_SIZE)}
          >
            <span>Load more</span> <PlusIcon />
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export default BlogHome
export function ErrorBoundary() {
  const error = useCapturedRouteError()
  console.error(error)
  return <ServerError />
}
